import { useState } from "react";
import { useNavigate } from "react-router-dom";

const BRAND = "#FF6B35";

type PostStatus = "published" | "draft" | "under_review" | "rejected";

interface MyPost {
  id: number;
  title: string;
  cover: string;
  status: PostStatus;
  publishTime: string;
  likes: number;
  comments: number;
  views: number;
  location: string;
  tags: string[];
  draftContent?: string;
}

const statusConfig: Record<PostStatus, { label: string; color: string; bg: string; icon: string }> = {
  published:    { label: "已发布", color: "#059669", bg: "#D1FAE5", icon: "ri-checkbox-circle-fill" },
  draft:        { label: "草稿",   color: "#92400E", bg: "#FEF3C7", icon: "ri-draft-line" },
  under_review: { label: "审核中", color: "#2563EB", bg: "#DBEAFE", icon: "ri-loader-4-line" },
  rejected:     { label: "被拒绝", color: "#DC2626", bg: "#FEE2E2", icon: "ri-close-circle-fill" },
};

const mockPosts: MyPost[] = [
  {
    id: 1,
    title: "三亚5天深度游：天涯海角·亚特兰蒂斯·蜈支洲岛完全攻略",
    cover: "https://readdy.ai/api/search-image?query=Sanya%20beach%20tropical%20paradise%20turquoise%20water%20white%20sand%20aerial%20view%20sunny%20day%20photography&width=400&height=240&seq=mp001&orientation=landscape",
    status: "published",
    publishTime: "2025-04-10",
    likes: 1234,
    comments: 89,
    views: 15600,
    location: "三亚",
    tags: ["三亚", "海岛", "亲子游"],
  },
  {
    id: 2,
    title: "大理四日慢生活：洱海骑行、古城漫步与美食探店",
    cover: "https://readdy.ai/api/search-image?query=Dali%20Yunnan%20Erhai%20lake%20bicycle%20ride%20serene%20morning%20mist%20traditional%20architecture%20photography&width=400&height=240&seq=mp002&orientation=landscape",
    status: "published",
    publishTime: "2025-03-22",
    likes: 876,
    comments: 52,
    views: 9800,
    location: "大理",
    tags: ["大理", "骑行", "美食"],
  },
  {
    id: 3,
    title: "成都两天一夜：熊猫基地+宽窄巷子+串串香完整路线",
    cover: "https://readdy.ai/api/search-image?query=Chengdu%20giant%20panda%20base%20bamboo%20forest%20cute%20panda%20eating%20bamboo%20travel%20photography%20warm%20light&width=400&height=240&seq=mp003&orientation=landscape",
    status: "under_review",
    publishTime: "2025-04-14",
    likes: 0,
    comments: 0,
    views: 0,
    location: "成都",
    tags: ["成都", "熊猫", "美食"],
  },
  {
    id: 4,
    title: "厦门鼓浪屿周末小记（未完成）",
    cover: "https://readdy.ai/api/search-image?query=Xiamen%20Gulangyu%20island%20colonial%20architecture%20ocean%20sea%20view%20sunny%20day%20travel%20photography&width=400&height=240&seq=mp004&orientation=landscape",
    status: "draft",
    publishTime: "2025-04-13",
    likes: 0,
    comments: 0,
    views: 0,
    location: "厦门",
    tags: ["厦门", "海岛"],
    draftContent: "鼓浪屿的早晨特别安静，岛上没有机动车...",
  },
  {
    id: 5,
    title: "新疆伊犁薰衣草花海骑马体验分享",
    cover: "https://readdy.ai/api/search-image?query=Xinjiang%20lavender%20field%20purple%20flowers%20horse%20riding%20grassland%20mountains%20scenic%20travel%20photography&width=400&height=240&seq=mp005&orientation=landscape",
    status: "rejected",
    publishTime: "2025-04-08",
    likes: 0,
    comments: 0,
    views: 0,
    location: "伊犁",
    tags: ["新疆", "薰衣草"],
  },
];

const tabFilters: { label: string; status?: PostStatus }[] = [
  { label: "全部" },
  { label: "已发布", status: "published" },
  { label: "审核中", status: "under_review" },
  { label: "草稿", status: "draft" },
  { label: "被拒绝", status: "rejected" },
];

function StatCard({ icon, val, label, color }: { icon: string; val: string | number; label: string; color: string }) {
  return (
    <div className="flex-1 flex flex-col items-center py-3 rounded-2xl" style={{ background: `${color}0D` }}>
      <i className={icon} style={{ color, fontSize: 18 }} />
      <div className="text-base font-bold mt-1" style={{ color }}>{val}</div>
      <div className="text-xs text-gray-400">{label}</div>
    </div>
  );
}

export default function MyPostsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [posts, setPosts] = useState<MyPost[]>(mockPosts);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [writeText, setWriteText] = useState("");
  const [writeTags, setWriteTags] = useState<string[]>([]);
  const [writeSuccess, setWriteSuccess] = useState(false);

  const filtered = activeTab === 0
    ? posts
    : posts.filter(p => p.status === tabFilters[activeTab].status);

  const totalViews = posts.filter(p => p.status === "published").reduce((s, p) => s + p.views, 0);
  const totalLikes = posts.filter(p => p.status === "published").reduce((s, p) => s + p.likes, 0);
  const totalComments = posts.filter(p => p.status === "published").reduce((s, p) => s + p.comments, 0);
  const publishedCount = posts.filter(p => p.status === "published").length;

  const handleDelete = (id: number) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = () => {
    if (deleteTargetId !== null) {
      setPosts(prev => prev.filter(p => p.id !== deleteTargetId));
    }
    setShowDeleteModal(false);
    setDeleteTargetId(null);
  };

  const handlePublish = () => {
    if (!writeText.trim()) return;
    const newPost: MyPost = {
      id: Date.now(),
      title: writeText.slice(0, 30) + (writeText.length > 30 ? "..." : ""),
      cover: "https://readdy.ai/api/search-image?query=travel%20diary%20journal%20cover%20scenic%20view%20natural%20landscape%20photography%20warm%20light&width=400&height=240&seq=mpnew&orientation=landscape",
      status: "under_review",
      publishTime: new Date().toISOString().slice(0, 10),
      likes: 0, comments: 0, views: 0,
      location: "未知地点",
      tags: writeTags,
    };
    setPosts(prev => [newPost, ...prev]);
    setWriteSuccess(true);
    setTimeout(() => {
      setWriteSuccess(false);
      setShowWriteModal(false);
      setWriteText("");
      setWriteTags([]);
    }, 1200);
  };

  const formatNum = (n: number) => n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n);

  return (
    <div className="min-h-screen pb-10" style={{ background: "#F7F7F7", fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div className="bg-white sticky top-0 z-40" style={{ borderBottom: "1px solid #F0F0F0" }}>
        <div className="px-4 pb-0 flex items-center gap-3"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer flex-shrink-0"
            style={{ background: "#F5F5F5" }}
          >
            <i className="ri-arrow-left-line text-gray-700" style={{ fontSize: 18 }} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">游记管理</h1>
          </div>
          <button
            onClick={() => setShowWriteModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap active:scale-95 transition-all"
            style={{ background: BRAND }}
          >
            <i className="ri-edit-2-line" style={{ fontSize: 14 }} />
            写游记
          </button>
        </div>

        {/* 数据统计 */}
        <div className="px-4 py-3 grid grid-cols-4 gap-2">
          <StatCard icon="ri-article-line" val={publishedCount} label="已发布" color={BRAND} />
          <StatCard icon="ri-eye-line" val={formatNum(totalViews)} label="总浏览" color="#2563EB" />
          <StatCard icon="ri-heart-3-line" val={formatNum(totalLikes)} label="获赞" color="#EF4444" />
          <StatCard icon="ri-chat-1-line" val={formatNum(totalComments)} label="评论" color="#10B981" />
        </div>

        {/* Tabs */}
        <div className="flex gap-5 px-4 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
          {tabFilters.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(i)}
              className="pb-3 text-sm cursor-pointer relative whitespace-nowrap flex-shrink-0"
              style={{ color: activeTab === i ? BRAND : "#ACACAC", fontWeight: activeTab === i ? 700 : 400 }}
            >
              {tab.label}
              {activeTab === i && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full" style={{ height: 2.5, width: 18, background: BRAND }} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Post list */}
      <div className="px-4 py-3 space-y-3">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-300">
            <i className="ri-article-line" style={{ fontSize: 48 }} />
            <div className="text-sm mt-3 text-gray-400">还没有{activeTab === 0 ? "" : tabFilters[activeTab].label}的游记</div>
            <button
              onClick={() => setShowWriteModal(true)}
              className="mt-4 px-6 py-2 rounded-full text-sm font-semibold text-white cursor-pointer"
              style={{ background: BRAND }}
            >
              去写游记
            </button>
          </div>
        )}
        {filtered.map(post => {
          const sc = statusConfig[post.status];
          return (
            <div key={post.id} className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
              {/* 封面图 */}
              <div className="relative overflow-hidden" style={{ height: 140 }}>
                <img src={post.cover} alt={post.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }} />
                {/* 状态标签 */}
                <div className="absolute top-3 left-3">
                  <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.color }}>
                    <i className={sc.icon} style={{ fontSize: 11 }} />
                    {sc.label}
                  </span>
                </div>
                {/* 位置 */}
                <div className="absolute bottom-3 left-3 flex items-center gap-1">
                  <i className="ri-map-pin-line text-white" style={{ fontSize: 11 }} />
                  <span className="text-xs text-white/90">{post.location}</span>
                </div>
              </div>

              {/* 内容 */}
              <div className="px-4 py-3">
                <div className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug mb-2">{post.title}</div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {post.tags.map(tag => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#FFF3EE", color: BRAND }}>#{tag}</span>
                  ))}
                </div>

                {/* 拒绝原因 */}
                {post.status === "rejected" && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-xl mb-3" style={{ background: "#FEE2E2" }}>
                    <i className="ri-error-warning-line flex-shrink-0 mt-0.5" style={{ color: "#DC2626", fontSize: 13 }} />
                    <span className="text-xs" style={{ color: "#DC2626" }}>内容不符合平台规范，请修改后重新提交</span>
                  </div>
                )}
                {/* 草稿预览 */}
                {post.status === "draft" && post.draftContent && (
                  <div className="text-xs text-gray-400 line-clamp-2 mb-3 px-3 py-2 rounded-xl" style={{ background: "#F9F9F9" }}>
                    {post.draftContent}
                  </div>
                )}

                {/* 数据行 */}
                {post.status === "published" && (
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-eye-line" style={{ fontSize: 12 }} />{formatNum(post.views)}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-heart-3-line" style={{ fontSize: 12 }} />{formatNum(post.likes)}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <i className="ri-chat-1-line" style={{ fontSize: 12 }} />{formatNum(post.comments)}
                    </div>
                    <div className="ml-auto text-xs text-gray-300">{post.publishTime}</div>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex gap-2">
                  {post.status === "published" && (
                    <button
                      onClick={() => navigate(`/post-detail?id=${post.id}`)}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                      style={{ background: "#FFF3EE", color: BRAND }}
                    >
                      <i className="ri-eye-line mr-1" style={{ fontSize: 12 }} />查看
                    </button>
                  )}
                  {(post.status === "draft" || post.status === "rejected") && (
                    <button
                      className="flex-1 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                      style={{ background: "#FFF3EE", color: BRAND }}
                    >
                      <i className="ri-edit-2-line mr-1" style={{ fontSize: 12 }} />编辑
                    </button>
                  )}
                  {post.status === "published" && (
                    <button
                      className="flex-1 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                      style={{ background: "#F0FDF4", color: "#059669" }}
                    >
                      <i className="ri-share-forward-line mr-1" style={{ fontSize: 12 }} />分享
                    </button>
                  )}
                  {post.status === "rejected" && (
                    <button
                      className="flex-1 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
                      style={{ background: "#DBEAFE", color: "#2563EB" }}
                    >
                      <i className="ri-send-plane-line mr-1" style={{ fontSize: 12 }} />重新提交
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer flex-shrink-0"
                    style={{ background: "#FEF2F2" }}
                  >
                    <i className="ri-delete-bin-6-line" style={{ color: "#EF4444", fontSize: 15 }} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete Confirm Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-3xl px-6 py-6 mx-5" style={{ maxWidth: 320 }}>
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ background: "#FEE2E2" }}>
              <i className="ri-delete-bin-6-line" style={{ color: "#EF4444", fontSize: 24 }} />
            </div>
            <div className="text-base font-bold text-gray-900 text-center mb-2">确认删除？</div>
            <div className="text-sm text-gray-400 text-center mb-5">删除后无法恢复，该游记将永久删除</div>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-2.5 rounded-full text-sm font-semibold cursor-pointer" style={{ background: "#F5F5F5", color: "#6B7280" }}>取消</button>
              <button onClick={confirmDelete} className="flex-1 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer" style={{ background: "#EF4444" }}>删除</button>
            </div>
          </div>
        </div>
      )}

      {/* Write Modal */}
      {showWriteModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}
          onClick={e => { if (e.target === e.currentTarget) setShowWriteModal(false); }}
        >
          <div className="w-full bg-white rounded-t-3xl" style={{ maxWidth: 480, margin: "0 auto" }}>
            <div className="w-9 h-1 rounded-full bg-gray-200 mx-auto mt-3" />
            <div className="flex items-center justify-between px-5 pt-4 pb-3" style={{ borderBottom: "1px solid #F0F0F0" }}>
              <button onClick={() => setShowWriteModal(false)} className="text-sm text-gray-400 cursor-pointer whitespace-nowrap">取消</button>
              <span className="text-base font-bold text-gray-900">发布游记</span>
              <button
                onClick={handlePublish}
                className="text-sm font-bold cursor-pointer whitespace-nowrap px-4 py-1.5 rounded-full transition-all"
                style={{ background: writeText.trim() ? BRAND : "#F3F4F6", color: writeText.trim() ? "#fff" : "#C0C4CC" }}
              >
                {writeSuccess ? "发布中…" : "发布"}
              </button>
            </div>
            <div className="px-5 pt-4 pb-8">
              <div className="flex gap-2.5 mb-4">
                <button className="w-[72px] h-[72px] rounded-2xl flex flex-col items-center justify-center flex-shrink-0 cursor-pointer" style={{ border: "1.5px dashed #FFB99A", background: "#FFF6F2" }}>
                  <i className="ri-image-add-line" style={{ color: BRAND, fontSize: 20 }} />
                  <span className="text-xs mt-1 font-medium" style={{ color: BRAND }}>图片</span>
                </button>
              </div>
              <textarea
                value={writeText}
                onChange={e => setWriteText(e.target.value.slice(0, 500))}
                placeholder="分享你的旅行故事..."
                className="w-full outline-none text-sm text-gray-700 resize-none leading-relaxed"
                style={{ minHeight: 80, background: "transparent" }}
              />
              <div className="text-xs text-gray-300 text-right mb-3">{writeText.length}/500</div>
              <div className="flex flex-wrap gap-2">
                {["三亚", "大理", "成都", "厦门", "亲子游", "蜜月"].map(t => (
                  <button
                    key={t}
                    onClick={() => setWriteTags(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])}
                    className="px-3 py-1 rounded-full text-xs cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      background: writeTags.includes(t) ? "#FFF3EE" : "#F5F5F5",
                      color: writeTags.includes(t) ? BRAND : "#888",
                      border: `1px solid ${writeTags.includes(t) ? "#FFB99A" : "transparent"}`,
                    }}
                  >#{t}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
